"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.fetchHistoricTVL = fetchHistoricTVL;
exports.putTVLDataToDynamoDB = putTVLDataToDynamoDB;
exports.deleteTVLDataFromDynamoDB = deleteTVLDataFromDynamoDB;
exports.getTVLDataFromDynamoDB = getTVLDataFromDynamoDB;
exports.getTVLLastDateFromDynamoDB = getTVLLastDateFromDynamoDB;
exports.getStartOfDayGMT = getStartOfDayGMT;
const alphafi_sdk_1 = require("@alphafi/alphafi-sdk");
const decimal_js_1 = require("decimal.js");
const client_dynamodb_1 = require("@aws-sdk/client-dynamodb");
const dailyRevenue_js_1 = require("./dailyRevenue.js");
const logger_js_1 = require("../../logger.js");
const TVL_TOKENS_TABLE_NAME = process.env.TVL_TOKENS_TABLE_NAME;
async function fetchHistoricTVL(params) {
    let result = [];
    const endTime = params.endTime
        ? params.endTime
        : getStartOfDayGMT(Date.now());
    const startTime = params.startTime
        ? params.startTime
        : getStartOfDayGMT(Date.now()) - 90 * 24 * 60 * 60 * 1000; //90 days ago
    if (startTime >= endTime)
        throw new Error("startTime must be less than endTime");
    const conf = (0, alphafi_sdk_1.getConf)();
    const newEventsStart = conf.NAVI_MODULE_LIQUIDITY_CHANGE_EVENT_START_TIME;
    if (endTime <= newEventsStart) {
        result = result.concat([]);
    }
    else if (startTime >= newEventsStart) {
        result = result.concat(await fetchHistoricalTVLModernEvents({ startTime, endTime }));
    }
    else {
        result = result.concat(await fetchHistoricalTVLModernEvents({
            startTime: getStartOfDayGMT(newEventsStart),
            endTime,
        }));
        result = result.concat([]);
    }
    return result;
}
async function fetchHistoricalTVLModernEvents(params) {
    const result = [];
    const { startTime, endTime } = params;
    const [liquidityChangeEvents, autocompoundEvents, withdrawV2Events, checkRatioEvents,] = await Promise.all([
        (0, alphafi_sdk_1.fetchLiquidityChangeEvents)({ startTime, endTime }),
        (0, alphafi_sdk_1.fetchAutoCompoundingEvents)({ startTime, endTime }),
        (0, alphafi_sdk_1.fetchWithdrawV2Events)({ startTime, endTime }),
        (0, alphafi_sdk_1.fetchCheckRatioEvents)({ startTime, endTime }),
    ]);
    // Remove autocompound events with the same timestamp as any liquidity change event or withdraw event
    const liquidityTimestamps = new Set(liquidityChangeEvents
        .map((event) => event.timestamp)
        .concat(withdrawV2Events.map((event) => event.timestamp)));
    let filteredAutocompoundEvents = autocompoundEvents.filter((event) => !liquidityTimestamps.has(event.timestamp));
    // remove new bluefin autobalance events as they do not show tvl
    filteredAutocompoundEvents = filteredAutocompoundEvents.filter((event) => {
        const investorPoolMap = (0, alphafi_sdk_1.getInvestorPoolMap)();
        const poolName = investorPoolMap.get(event.investor_id);
        if (!poolName) {
            (0, logger_js_1.logError)("PoolName not found for autocompound event", {
                component: "TVL",
                operation: "fetchHistoricalTVLModernEvents",
                context: { event },
            });
            return;
        }
        if (alphafi_sdk_1.poolInfo[poolName].strategyType === "AUTOBALANCE-LIQUIDITY-POOL" &&
            "amount" in event &&
            "total_amount_a" in event) {
            return false;
        }
        return true;
    });
    // make checkRatio events map
    const ratioMap = {};
    checkRatioEvents.forEach((event) => {
        const poolName = Object.keys(alphafi_sdk_1.poolInfo).find((poolName) => {
            return alphafi_sdk_1.poolInfo[poolName].checkRatioEventType === event.type
                ? true
                : false;
        });
        if (!poolName) {
            (0, logger_js_1.logError)("PoolName not found for checkRatio event", {
                component: "TVL",
                operation: "fetchHistoricalTVLModernEvents",
                context: { event },
            });
            throw new Error("PoolName not found for checkRatio event");
        }
        if (!ratioMap[poolName])
            ratioMap[poolName] = {};
        ratioMap[poolName][event.txDigest] = event.ratio;
    });
    // Sort in descending order
    const mergedEvents = [
        ...liquidityChangeEvents,
        ...filteredAutocompoundEvents,
        ...withdrawV2Events,
    ].sort((a, b) => b.timestamp - a.timestamp);
    // Group by pool in descending order
    const investorPoolMap = (0, alphafi_sdk_1.getInvestorPoolMap)();
    const eventsByPool = {};
    for (const event of mergedEvents) {
        let poolName;
        if ("pool_id" in event) {
            if (alphafi_sdk_1.defunctPoolsSet.has(event.pool_id)) {
                // discontinued NAVI-LOOP-SUI-VSUI
                continue;
            }
            poolName = alphafi_sdk_1.poolIdPoolNameMap[event.pool_id];
            if (!poolName)
                continue;
        }
        else if ("investor_id" in event) {
            const poolNameFromInvestor = investorPoolMap.get(event.investor_id);
            if (!poolNameFromInvestor) {
                (0, logger_js_1.logWarning)("Investor id not found in investorPoolMap", {
                    component: "TVL",
                    operation: "fetchHistoricalTVLModernEvents",
                    context: { investorId: event.investor_id },
                });
                continue;
            }
            poolName = poolNameFromInvestor;
        }
        else {
            throw new Error("Unknown event type found");
        }
        eventsByPool[poolName] = eventsByPool[poolName]
            ? eventsByPool[poolName].concat([event])
            : [event];
    }
    // Populate result array with the last event of each day for each pool
    const oneDayInMs = 24 * 60 * 60 * 1000;
    for (let time = (0, dailyRevenue_js_1.getEndOfDayGMT)(endTime); time >= (0, dailyRevenue_js_1.getEndOfDayGMT)(startTime); time -= oneDayInMs) {
        for (const poolName in eventsByPool) {
            if (poolName === "ALPHA")
                continue;
            const poolEvents = eventsByPool[poolName];
            if (!poolEvents) {
                (0, logger_js_1.logInfo)("No events found for pool", {
                    component: "TVL",
                    operation: "fetchHistoricalTVLModernEvents",
                    context: { poolName },
                });
                continue;
            }
            // Traverse from the start of events for each pool to find the last event of the day
            let lastEventOfDay = undefined;
            while (poolEvents.length > 0) {
                const event = poolEvents[0];
                if ((0, dailyRevenue_js_1.getEndOfDayGMT)(event.timestamp) === time) {
                    lastEventOfDay = event;
                    poolEvents.shift();
                    break;
                }
                else if ((0, dailyRevenue_js_1.getEndOfDayGMT)(event.timestamp) > time) {
                    poolEvents.shift();
                }
                else {
                    break;
                }
            }
            // handling of special case: Navi-loop-usdc-usdt check ratio briefly unavailable in autocompound
            if (poolName === "NAVI-LOOP-USDC-USDT" &&
                lastEventOfDay &&
                lastEventOfDay.type === alphafi_sdk_1.poolInfo[poolName].autoCompoundingEventType &&
                !ratioMap[poolName][lastEventOfDay.txDigest]) {
                // find the last occuring liquidity change
                while (poolEvents.length > 0) {
                    const event = poolEvents[0];
                    if (event.type !== alphafi_sdk_1.poolInfo[poolName].liquidityChangeEventType) {
                        poolEvents.shift();
                        continue;
                    }
                    if ((0, dailyRevenue_js_1.getEndOfDayGMT)(event.timestamp) === time) {
                        lastEventOfDay = event;
                        poolEvents.shift();
                        break;
                    }
                    else if ((0, dailyRevenue_js_1.getEndOfDayGMT)(event.timestamp) > time) {
                        poolEvents.shift();
                    }
                    else {
                        break;
                    }
                }
            }
            // Add the last event's total to the daily sum if it exists
            if (lastEventOfDay) {
                if ("pool_id" in lastEventOfDay) {
                    // liquidity change event or withdrawV2 event
                    if ("total_amount_a" in lastEventOfDay) {
                        const { coinA, coinB } = (0, alphafi_sdk_1.coinsInPool)(poolName);
                        const amountA = new decimal_js_1.Decimal(lastEventOfDay.total_amount_a).div(new decimal_js_1.Decimal(10).pow(new decimal_js_1.Decimal(alphafi_sdk_1.coinsList[coinA].expo)));
                        const amountB = new decimal_js_1.Decimal(lastEventOfDay.total_amount_b).div(new decimal_js_1.Decimal(10).pow(new decimal_js_1.Decimal(alphafi_sdk_1.coinsList[coinB].expo)));
                        result.push({
                            time: time,
                            poolName: poolName,
                            coin: coinA,
                            amount: amountA.toString(),
                        });
                        result.push({
                            time: time,
                            poolName: poolName,
                            coin: coinB,
                            amount: amountB.toString(),
                        });
                    }
                    else if ("tokens_invested" in lastEventOfDay) {
                        const coin = (0, alphafi_sdk_1.coinsInPool)(poolName, {
                            type: alphafi_sdk_1.poolInfo[poolName].liquidityChangeEventType,
                            event_type: 0,
                        });
                        let amount = new decimal_js_1.Decimal(-1);
                        if (alphafi_sdk_1.poolInfo[poolName].strategyType === "LOOPING" ||
                            alphafi_sdk_1.poolInfo[poolName].strategyType === "SINGLE-LOOPING") {
                            const ratio = ratioMap[poolName]?.[lastEventOfDay.txDigest] || "0";
                            if (!ratio) {
                                (0, logger_js_1.logError)("Ratio not found for looping pool liquidity change event with tokens_invested", {
                                    component: "TVL",
                                    operation: "fetchHistoricalTVLModernEvents",
                                    context: { poolName, lastEventOfDay },
                                });
                                throw new Error("ratio not found");
                            }
                            amount = new decimal_js_1.Decimal(lastEventOfDay.tokens_invested)
                                .mul(new decimal_js_1.Decimal(1).minus(new decimal_js_1.Decimal(ratio).div(new decimal_js_1.Decimal(1e20))))
                                .div(alphafi_sdk_1.poolInfo[poolName].parentProtocolName === "NAVI"
                                ? new decimal_js_1.Decimal(1e9)
                                : new decimal_js_1.Decimal(10).pow(alphafi_sdk_1.coinsList[coin].expo));
                        }
                        else {
                            amount = new decimal_js_1.Decimal(lastEventOfDay.tokens_invested).div(1e9);
                        }
                        result.push({
                            time: time,
                            poolName: poolName,
                            coin: coin,
                            amount: amount.toString(),
                        });
                    }
                    else {
                        (0, logger_js_1.logError)("Unexpected liquidity change/withdraw event encountered", {
                            component: "TVL",
                            operation: "fetchHistoricalTVLModernEvents",
                            context: { lastEventOfDay },
                        });
                        throw new Error("unexpected event encountered");
                    }
                }
                else if ("investor_id" in lastEventOfDay) {
                    // autocompound event
                    if ("total_amount_a" in lastEventOfDay) {
                        //CETUS
                        const { coinA, coinB } = (0, alphafi_sdk_1.coinsInPool)(poolName);
                        const amountA = new decimal_js_1.Decimal(lastEventOfDay.total_amount_a.toString()).div(new decimal_js_1.Decimal(10).pow(new decimal_js_1.Decimal(alphafi_sdk_1.coinsList[coinA].expo)));
                        const amountB = new decimal_js_1.Decimal(lastEventOfDay.total_amount_b.toString()).div(new decimal_js_1.Decimal(10).pow(new decimal_js_1.Decimal(alphafi_sdk_1.coinsList[coinB].expo)));
                        result.push({
                            time: time,
                            poolName: poolName,
                            coin: coinA,
                            amount: amountA.toString(),
                        });
                        result.push({
                            time: time,
                            poolName: poolName,
                            coin: coinB,
                            amount: amountB.toString(),
                        });
                    }
                    else if ("total_amount" in lastEventOfDay) {
                        // NAVI, NAVI-LOOP, BUCKET-BUCKET, ALPHALEND-LOOP
                        const coin = (0, alphafi_sdk_1.coinsInPool)(poolName);
                        // NAVI has everything scaled to 9 decimals
                        let amount = new decimal_js_1.Decimal(-1);
                        if (alphafi_sdk_1.poolInfo[poolName].strategyType === "LOOPING") {
                            const ratio = ratioMap[poolName]?.[lastEventOfDay.txDigest] || "0";
                            if (!ratio) {
                                (0, logger_js_1.logError)("Ratio not found for looping pool autocompound event with total_amount", {
                                    component: "TVL",
                                    operation: "fetchHistoricalTVLModernEvents",
                                    context: { poolName, lastEventOfDay },
                                });
                                throw new Error("ratio not found");
                            }
                            amount = new decimal_js_1.Decimal(lastEventOfDay.total_amount.toString())
                                .mul(new decimal_js_1.Decimal(1).minus(new decimal_js_1.Decimal(ratio).div(new decimal_js_1.Decimal(1e20))))
                                .div(alphafi_sdk_1.poolInfo[poolName].parentProtocolName === "NAVI"
                                ? new decimal_js_1.Decimal(1e9)
                                : new decimal_js_1.Decimal(10).pow(alphafi_sdk_1.coinsList[coin].expo));
                        }
                        else {
                            amount = new decimal_js_1.Decimal(lastEventOfDay.total_amount.toString()).div(1e9);
                        }
                        result.push({
                            time: time,
                            poolName: poolName,
                            coin: coin,
                            amount: amount.toString(),
                        });
                    }
                    else {
                        (0, logger_js_1.logError)("Unknown parent protocol found in autocompound event", {
                            component: "TVL",
                            operation: "fetchHistoricalTVLModernEvents",
                            context: { lastEventOfDay },
                        });
                        throw new Error("unknown parent protocol found");
                    }
                }
                else {
                    (0, logger_js_1.logError)("Unexpected event encountered in TVL processing", {
                        component: "TVL",
                        operation: "fetchHistoricalTVLModernEvents",
                        context: { lastEventOfDay },
                    });
                    throw new Error("unexpected event encountered");
                }
            }
        }
        if ("ALPHA" in eventsByPool) {
            const poolEvents = eventsByPool["ALPHA"];
            if (!poolEvents) {
                (0, logger_js_1.logInfo)("No events found for ALPHA pool", {
                    component: "TVL",
                    operation: "fetchHistoricalTVLModernEvents",
                });
                continue;
            }
            let tailAutoCompound = new decimal_js_1.Decimal(0);
            let lastDepositWithdrawTotal = new decimal_js_1.Decimal(0);
            while (poolEvents.length > 0) {
                const event = poolEvents[0];
                if ((0, dailyRevenue_js_1.getEndOfDayGMT)(event.timestamp) === time) {
                    if ("investor_id" in event &&
                        !("total_amount" in event) &&
                        !("total_amount_a" in event)) {
                        tailAutoCompound = tailAutoCompound.add(new decimal_js_1.Decimal(event.amount).div(1e9));
                        poolEvents.shift();
                    }
                    else if ("pool_id" in event &&
                        "tokens_invested" in event &&
                        !("total_amount_a" in event)) {
                        lastDepositWithdrawTotal = new decimal_js_1.Decimal(event.tokens_invested).div(1e9);
                        poolEvents.shift();
                        break;
                    }
                    else {
                        (0, logger_js_1.logError)("Unexpected event found in ALPHA pool processing", {
                            component: "TVL",
                            operation: "fetchHistoricalTVLModernEvents",
                            context: { event },
                        });
                        throw new Error("Unexpected Event Found");
                    }
                }
                else if ((0, dailyRevenue_js_1.getEndOfDayGMT)(event.timestamp) > time) {
                    poolEvents.shift();
                }
                else {
                    break;
                }
            }
            result.push({
                time: time,
                poolName: "ALPHA",
                coin: "ALPHA",
                amount: tailAutoCompound.add(lastDepositWithdrawTotal).toString(),
            });
        }
    }
    return result.map((o) => {
        const amount = o.amount ? o.amount : "0";
        return { ...o, amount };
    });
}
// TODO: improve error logging, maybe from updateDataBase
// TODO: Discuss: current method of deletion takes lot of capacity units, either improve or leave data as is
async function putTVLDataToDynamoDB(params) {
    const tableName = params.tableName ? params.tableName : TVL_TOKENS_TABLE_NAME;
    const ddbClient = params.ddbClient
        ? params.ddbClient
        : new client_dynamodb_1.DynamoDBClient({ region: process.env.AWS_REGION });
    const tvlItems = params.TVL.map((tvlObject) => {
        return {
            time: tvlObject.time,
            amount: tvlObject.amount,
            poolName_coin: `${tvlObject.poolName}#${tvlObject.coin}`,
        };
    });
    if (!tableName) {
        (0, logger_js_1.logError)("TVL tokens table name not configured for put operation", {
            component: "TVL",
            operation: "putTVLDataToDynamoDB",
            context: {
                message: "set TVL_TOKENS_TABLE_NAME as env variable or pass in params",
            },
        });
        throw new Error("table name not found");
    }
    let inputItemsLength = 0;
    while (tvlItems.length > 0) {
        // DynamoDB BatchWrite supports a max of 25 items per batch
        const batch = tvlItems.splice(0, 25);
        inputItemsLength += batch.length;
        // Ensure each PutRequest is defined and matches the structure
        const writeItems = {
            [tableName]: batch.map((tvlItem) => ({
                PutRequest: {
                    Item: {
                        poolName_coin: { S: tvlItem.poolName_coin }, // Partition key
                        time: { N: tvlItem.time.toString() }, // Sort key
                        amount: { S: tvlItem.amount },
                    },
                },
            })),
        };
        const params = {
            RequestItems: writeItems,
            ReturnConsumedCapacity: "TOTAL",
            ReturnItemCollectionMetrics: "SIZE",
        };
        try {
            let unprocessedItems = undefined;
            // Retry unprocessed items until all are processed
            do {
                const response = await ddbClient.send(new client_dynamodb_1.BatchWriteItemCommand(params));
                // Check if there are any unprocessed items
                unprocessedItems = response.UnprocessedItems;
                if (unprocessedItems && Object.keys(unprocessedItems).length > 0) {
                    (0, logger_js_1.logInfo)("Retrying unprocessed items in TVL put batch write", {
                        component: "TVL",
                        operation: "putTVLDataToDynamoDB",
                        context: {
                            unprocessedItemsCount: Object.keys(unprocessedItems).length,
                        },
                    });
                    params.RequestItems = unprocessedItems; // Reassign unprocessed items to retry
                }
            } while (unprocessedItems && Object.keys(unprocessedItems).length > 0);
        }
        catch (err) {
            (0, logger_js_1.logError)("BatchWriteItemCommand failed in TVL put operation", {
                component: "TVL",
                operation: "putTVLDataToDynamoDB",
                error: err instanceof Error ? err : new Error(String(err)),
            });
        }
    }
    (0, logger_js_1.logInfo)("TVL data put operation completed", {
        component: "TVL",
        operation: "putTVLDataToDynamoDB",
        context: { itemsToPut: inputItemsLength },
    });
}
async function deleteTVLDataFromDynamoDB(params) {
    // the entries corresponding to timeRange values are deleted
    if (params.timeRange.min > params.timeRange.max) {
        (0, logger_js_1.logError)("Incorrect time range provided for TVL deletion", {
            component: "TVL",
            operation: "deleteTVLDataFromDynamoDB",
            context: { minTime: params.timeRange.min, maxTime: params.timeRange.max },
        });
        throw new Error("incorrect time range");
    }
    const tableName = params.tableName ? params.tableName : TVL_TOKENS_TABLE_NAME;
    if (!tableName) {
        (0, logger_js_1.logError)("TVL tokens table name not configured for delete operation", {
            component: "TVL",
            operation: "deleteTVLDataFromDynamoDB",
            context: {
                message: "set TVL_TOKENS_TABLE_NAME as env variable or pass in params",
            },
        });
        throw new Error("table name not found");
    }
    const ddbClient = params.ddbClient
        ? params.ddbClient
        : new client_dynamodb_1.DynamoDBClient({ region: process.env.AWS_REGION });
    const poolName_coin = new Set();
    const poolNames = params.poolNames ? params.poolNames : Object.keys(alphafi_sdk_1.poolInfo);
    poolNames.forEach((poolName) => {
        if (["ALPHAFI", "BUCKET", "NAVI", "ALPHALEND"].includes(alphafi_sdk_1.poolInfo[poolName].parentProtocolName)) {
            const coin = (0, alphafi_sdk_1.coinsInPool)(poolName);
            poolName_coin.add(`${poolName}#${coin}`);
            // some looping pools require both the coins as keys
            poolName_coin.add(`${poolName}#${(0, alphafi_sdk_1.coinsInPool)(poolName, {
                type: alphafi_sdk_1.poolInfo[poolName].liquidityChangeEventType,
                event_type: 0,
            })}`);
        }
        else if (["CETUS", "BLUEFIN"].includes(alphafi_sdk_1.poolInfo[poolName].parentProtocolName)) {
            const coinA = alphafi_sdk_1.doubleAssetPoolCoinMap[poolName].coin1;
            const coinB = alphafi_sdk_1.doubleAssetPoolCoinMap[poolName].coin2;
            poolName_coin.add(`${poolName}#${coinA}`);
            poolName_coin.add(`${poolName}#${coinB}`);
        }
        else {
            (0, logger_js_1.logError)("Unhandled protocol found during TVL deletion", {
                component: "TVL",
                operation: "deleteTVLDataFromDynamoDB",
                context: {
                    poolName,
                    parentProtocol: alphafi_sdk_1.poolInfo[poolName].parentProtocolName,
                },
            });
            throw new Error("Unhandled Protocol found");
        }
    });
    const items = [];
    for (let day = (0, dailyRevenue_js_1.getEndOfDayGMT)(params.timeRange.min); day <= (0, dailyRevenue_js_1.getEndOfDayGMT)(params.timeRange.max); day += 24 * 60 * 60 * 1000) {
        for (const key of Array.from(poolName_coin)) {
            items.push({ time: day, poolName_coin: key });
        }
    }
    let inputItemsLength = 0;
    while (items.length > 0) {
        // DynamoDB BatchWrite supports a max of 25 items per batch
        const batch = items.splice(0, 25);
        inputItemsLength += batch.length;
        // Prepare DeleteRequest for each item
        const requestItems = {
            [tableName]: batch.map((tvlItem) => ({
                DeleteRequest: {
                    Key: {
                        poolName_coin: { S: tvlItem.poolName_coin }, // Partition key
                        time: { N: tvlItem.time.toString() }, // Sort key
                    },
                },
            })),
        };
        const params = {
            RequestItems: requestItems,
            ReturnConsumedCapacity: "TOTAL",
            ReturnItemCollectionMetrics: "SIZE",
        };
        try {
            let unprocessedItems = undefined;
            // Retry unprocessed items until all are processed
            do {
                const response = await ddbClient.send(new client_dynamodb_1.BatchWriteItemCommand(params));
                // Check if there are any unprocessed items
                unprocessedItems = response.UnprocessedItems;
                if (unprocessedItems && Object.keys(unprocessedItems).length > 0) {
                    (0, logger_js_1.logInfo)("Retrying unprocessed items in TVL delete batch write", {
                        component: "TVL",
                        operation: "deleteTVLDataFromDynamoDB",
                        context: {
                            unprocessedItemsCount: Object.keys(unprocessedItems).length,
                        },
                    });
                    params.RequestItems = unprocessedItems; // Reassign unprocessed items to retry
                }
            } while (unprocessedItems && Object.keys(unprocessedItems).length > 0);
        }
        catch (err) {
            (0, logger_js_1.logError)("BatchWriteItemCommand failed in TVL delete operation", {
                component: "TVL",
                operation: "deleteTVLDataFromDynamoDB",
                error: err instanceof Error ? err : new Error(String(err)),
            });
        }
    }
    (0, logger_js_1.logInfo)("TVL data delete operation completed", {
        component: "TVL",
        operation: "deleteTVLDataFromDynamoDB",
        context: { itemsDeleted: inputItemsLength },
    });
}
// Test if can do || in keys and do it in 1 query
async function getTVLDataFromDynamoDB(params) {
    const tableName = params.tableName ? params.tableName : TVL_TOKENS_TABLE_NAME;
    const ddbClient = params.ddbClient
        ? params.ddbClient
        : new client_dynamodb_1.DynamoDBClient({ region: process.env.AWS_REGION });
    const keys = params.poolName_coin
        ? params.poolName_coin
        : (() => {
            const poolName_coin = new Set();
            Object.keys(alphafi_sdk_1.poolInfo).forEach((poolName) => {
                if (["ALPHAFI", "BUCKET", "NAVI", "ALPHALEND"].includes(alphafi_sdk_1.poolInfo[poolName].parentProtocolName)) {
                    const coin = (0, alphafi_sdk_1.coinsInPool)(poolName);
                    poolName_coin.add(`${poolName}#${coin}`);
                    // some looping pools require both the coins as keys
                    poolName_coin.add(`${poolName}#${(0, alphafi_sdk_1.coinsInPool)(poolName, {
                        type: alphafi_sdk_1.poolInfo[poolName].liquidityChangeEventType,
                        event_type: 0,
                    })}`);
                }
                else if (["CETUS", "BLUEFIN"].includes(alphafi_sdk_1.poolInfo[poolName].parentProtocolName)) {
                    const { coinA, coinB } = (0, alphafi_sdk_1.coinsInPool)(poolName);
                    poolName_coin.add(`${poolName}#${coinA}`);
                    poolName_coin.add(`${poolName}#${coinB}`);
                }
                else {
                    (0, logger_js_1.logError)("Unhandled protocol found during TVL data retrieval", {
                        component: "TVL",
                        operation: "getTVLDataFromDynamoDB",
                        context: { poolName },
                    });
                    throw new Error("Unhandled Protocol found");
                }
            });
            return Array.from(poolName_coin);
        })();
    //verify keys
    keys.forEach((key) => {
        const [, coin] = key.split("#");
        if (!coin || !Object.keys(alphafi_sdk_1.coinsList).includes(coin)) {
            (0, logger_js_1.logError)("Incorrect key format for TVL data retrieval", {
                component: "TVL",
                operation: "getTVLDataFromDynamoDB",
                context: { key, coin },
            });
            throw new Error("Incorrect key format");
        }
    });
    const commands = keys.map((key) => {
        const command = new client_dynamodb_1.QueryCommand({
            TableName: tableName,
            KeyConditionExpression: "poolName_coin = :poolName_coin", // Partition key condition
            ExpressionAttributeValues: {
                ":poolName_coin": { S: key },
            },
        });
        return command;
    });
    try {
        let items = [];
        for (const command of commands) {
            const response = await ddbClient.send(command);
            if (response.Items)
                items = items.concat(response.Items);
        }
        return items;
    }
    catch (error) {
        (0, logger_js_1.logError)("Failed to fetch TVL items by pool name", {
            component: "TVL",
            operation: "getTVLDataFromDynamoDB",
            error: error instanceof Error ? error : new Error(String(error)),
        });
        throw error;
    }
}
async function getTVLLastDateFromDynamoDB(params) {
    const tableName = params.tableName ? params.tableName : TVL_TOKENS_TABLE_NAME;
    const ddbClient = params.ddbClient
        ? params.ddbClient
        : new client_dynamodb_1.DynamoDBClient({ region: process.env.AWS_REGION });
    const key = "ALPHA#ALPHA";
    const alphaTvlData = await getTVLDataFromDynamoDB({
        tableName,
        ddbClient,
        poolName_coin: [key],
    });
    const lastTime = Number(alphaTvlData
        .map((o) => o.time.N)
        .filter((time) => (0, dailyRevenue_js_1.getEndOfDayGMT)(Number(time)) === Number(time))
        .sort((a, b) => Number(b) - Number(a))[0]);
    return lastTime;
}
function getStartOfDayGMT(timestamp) {
    const now = new Date(timestamp);
    now.setUTCHours(0, 0, 0, 0);
    return now.getTime();
}
//# sourceMappingURL=TVL.js.map